import CloseIcon from "assets/images/CloseIcon";
import classNames from "classnames";
import {useState, useEffect} from "react";
import ReactModal from "react-modal";
import {common as stringsCommon, datePickerModal as strings} from "strings";

import Button from "components/Button";
import DatePicker from "components/DatePicker";
import LoadingIndicator from "components/LoadingIndicator";

import styles from "./DatePickerModal.module.css";

type DatePickerModalProps = {
  visible: boolean;
  onClickConfirm: (dateChosen: Date) => void;
  onClickCancel: () => void;
  datePicked?: Date | undefined;
  availableDates?: Date[];
  isLoading?: boolean;
  arePreviousDatesDisabled?: boolean;
  enableCurrentDate?: boolean;
  isDateAvailable?: (dateToCheck: Date) => boolean;
};

const getStartingSelectedDate = (
  availableDates: Date[] | undefined,
  date: Date | undefined,
) => {
  if (!date) return undefined;
  if (!availableDates) return date;

  date.setHours(0, 0, 0, 0);

  return (
    availableDates.find((d) => {
      d.setHours(0, 0, 0, 0);
      return date.toISOString() === d.toISOString();
    }) ?? undefined
  );
};

export default function DatePickerModal(props: DatePickerModalProps) {
  const [selectedDate, setSelectedDate] = useState(
    getStartingSelectedDate(props.availableDates, props.datePicked),
  );

  useEffect(() => {
    setSelectedDate(props.datePicked);
  }, [props.datePicked, props.visible]);

  return (
    <ReactModal
      className={styles.modal}
      overlayClassName={styles.overlay}
      isOpen={props.visible}
    >
      <div className={styles.modalContent}>
        <button
          className={styles.close}
          onClick={() => {
            return props.onClickCancel();
          }}
        >
          <CloseIcon />
        </button>
        {props.isLoading ? (
          <LoadingIndicator className={styles.loadingIndicator} />
        ) : null}
        <div
          className={classNames(
            styles.datePicker,
            props.isLoading && styles.hidden,
          )}
        >
          <DatePicker
            datePicked={selectedDate}
            onDateChange={setSelectedDate}
            availableDates={props.availableDates}
            arePreviousDatesDisabled={props.arePreviousDatesDisabled}
            enableCurrentDate={props.enableCurrentDate}
            isDateAvailable={props.isDateAvailable}
          />
          <Button
            className={styles.selectButton}
            disabled={!selectedDate}
            onClick={() => {
              if (!selectedDate) return;

              props.onClickConfirm(selectedDate);
            }}
          >
            {strings.select}
          </Button>
          <Button
            className={styles.closeButton}
            variant="minimal"
            onClick={props.onClickCancel}
          >
            {stringsCommon.close}
          </Button>
        </div>
      </div>
    </ReactModal>
  );
}
